import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import RowActionsMenu from '../../Components/RowActionsMenu';

const SalePriceListManagement = () => {
    const [salePriceLists, setSalePriceLists] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            handleLogout();
            return null;
        }
        return JSON.parse(user)?.token;
    }, [handleLogout]);

    const fetchSalePriceLists = useCallback(async () => {
        const token = checkToken();
        if (!token) return;

        try {
            const response = await fetch(`${API_BASE_URL}/seller/api/default-prices/with-user`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 403) {
                    handleLogout();
                    return;
                }
                const errorText = await response.text();
                setError(`Failed to fetch sale price lists: ${errorText}`);
                throw new Error(`Failed to fetch sale price lists: ${errorText}`);
            }

            const data = await response.json();
            setSalePriceLists(data || []);
        } catch (error) {
            setError('Error fetching sale price lists');
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [checkToken, handleLogout]);

    useEffect(() => {
        fetchSalePriceLists();
    }, [fetchSalePriceLists]);

    const groupedData = useMemo(() => {
        const map = new Map();
        (salePriceLists || []).forEach(item => {
            const key = item.sellerId || 'UNKNOWN';
            if (!map.has(key)) {
                map.set(key, {
                    sellerId: item.sellerId,
                    sellerName: item.sellerName,
                    iceName: item.iceName,
                    items: []
                });
            }
            const group = map.get(key);
            if (!group.sellerName && item.sellerName) {
                group.sellerName = item.sellerName;
            }
            group.items.push({ id: item.id, iceCode: item.iceCode , iceName: item.iceName, price: item.price });
        });
        let arr = Array.from(map.values());
        if (searchTerm && searchTerm.trim()) {
            const q = searchTerm.trim().toLowerCase();
            arr = arr.filter(g => (g.sellerName || '').toLowerCase().includes(q));
        }
        return arr;
    }, [salePriceLists, searchTerm]);

    return (
        <div className="btc-container">
            <button className="btc-register-button" onClick={() => navigate('/salePriceList/create')}>
                ตั้งราคาขายประจำสาย
            </button>

            <div style={{ margin: '12px 0' }}>
                <input
                    type="text"
                    placeholder="ค้นหาตามชื่อผู้ขาย (Seller Name)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '100%', maxWidth: 360, padding: 8 }}
                />
            </div>

            {loading ? (
                <p>Loading...</p>
            ) : (
                <>
                    {error && <p className="error">{error}</p>}
                    <table className="btc-table">
                        <thead>
                        <tr>
                            <th>Seller ID</th>
                            <th>Seller Name</th>
                            <th>ICE Codes</th>
                            <th>Prices</th>
                            <th>จัดการ</th>
                        </tr>
                        </thead>
                        <tbody>
                        {groupedData.map((group) => (
                            <tr key={group.sellerId}>
                                <td>{group.sellerId}</td>
                                <td>{group.sellerName}</td>
                                <td style={{ textAlign: 'right', width: '10%' }}>
                                    {group.items.map((item, idx) => (
                                        <div key={item.iceCode || idx}>{item.iceCode} {item.iceName}</div>
                                    ))}
                                </td>
                                <td style={{ textAlign: 'left', width: '10%' }}>
                                    {group.items.map((item, idx) => (
                                        <div key={(item.iceCode || idx) + 'p'}>{item.price} บาท</div>
                                    ))}
                                </td>
                                <td>
                                    <RowActionsMenu
                                        actions={[
                                            {
                                                label: 'แก้ไข',
                                                icon: <i className="fas fa-edit"></i>,
                                                onClick: () => navigate(`/salePriceList/user/${group.sellerId}`, {
                                                    state: { sellerName: group.sellerName },
                                                }),
                                            },
                                        ]}
                                    />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default SalePriceListManagement;
